import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { compliance, ia } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Plus, Sparkles, Loader2, ArrowRight, AlertTriangle, ShieldCheck, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  ABERTO: "Aberto", EM_TRIAGEM: "Em Triagem", EM_INVESTIGACAO: "Em Investigação",
  MANIFESTACAO: "Manifestação", EM_ANALISE: "Em Análise", PARECER_EMITIDO: "Parecer Emitido",
  DECISAO_TOMADA: "Decisão", CONCLUIDO: "Concluído", ARQUIVADO: "Arquivado"
};

export function OcorrenciasList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => compliance.processos(filter).then(r => setItems(r.data));

  useEffect(() => { load(); }, [filter]);

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8">
          <div>
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.3em] uppercase mb-3">
              Compliance · Processos
            </div>
            <h1 className="font-heading text-4xl font-light text-white">Ocorrências & Processos</h1>
          </div>
          <Link
            to="/compliance/ocorrencias/nova"
            data-testid="btn-nova-ocorrencia-lista"
            className="inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-5 py-3 rounded-lg transition-colors"
          >
            <Plus size={16} /> Nova Ocorrência
          </Link>
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <div className="text-[#A1A4C7] text-xs uppercase tracking-widest font-bold flex items-center gap-1 shrink-0">
            <Filter size={13} /> Filtrar:
          </div>
          {["", "ABERTO", "EM_INVESTIGACAO", "PARECER_EMITIDO", "CONCLUIDO"].map(s => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap transition ${
                filter === s
                  ? "bg-[#B28F4B] text-[#1A1D42]"
                  : "bg-[#131532] text-[#A1A4C7] hover:bg-[#1F2350]"
              }`}
            >
              {s === "" ? "Todos" : STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="grid gap-3">
          {items.length === 0 && (
            <div className="soeasy-card p-10 text-center">
              <div className="text-white text-lg font-heading mb-1">Nenhum processo</div>
              <div className="text-[#A1A4C7] text-sm">Ajuste o filtro ou crie uma nova ocorrência</div>
            </div>
          )}
          {items.map(p => (
            <Link
              key={p.id}
              to={`/compliance/processos/${p.id}`}
              data-testid={`ocorrencia-item-${p.id}`}
              className="soeasy-card p-5 flex flex-col md:flex-row md:items-center gap-4 group"
            >
              <div className="font-mono text-[#B28F4B] text-sm font-bold shrink-0 md:w-40">{p.protocolo}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold group-hover:text-[#B28F4B] transition-colors">{p.titulo}</div>
                <div className="text-xs text-[#A1A4C7] mt-1 line-clamp-1">{p.descricao}</div>
                <div className="flex flex-wrap gap-3 mt-2 text-[10px] font-bold uppercase tracking-wider text-[#6B6E8F]">
                  <span>Gravidade: <span className={`${p.gravidade === "critica" ? "text-red-400" : p.gravidade === "alta" ? "text-orange-400" : "text-[#B28F4B]"}`}>{p.gravidade}</span></span>
                  <span>Origem: {p.origem}</span>
                </div>
              </div>
              <span className={`status-pill status-${p.status}`}>{STATUS_LABEL[p.status] || p.status}</span>
            </Link>
          ))}
        </div>
      </div>
    </Shell>
  );
}

export function NovaOcorrencia() {
  const [form, setForm] = useState({
    titulo: "", descricao_fato: "", gravidade: "media",
    local: "", data_fato: "", setor_envolvido: "",
    colaborador_investigado: "", origem: "manual", anonimo: false,
  });
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const analisarIA = async () => {
    if (!form.descricao_fato || form.descricao_fato.length < 20) {
      toast.error("Descreva o fato com mais detalhes (mínimo 20 caracteres)");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await ia.classificar(form.descricao_fato);
      setAiResult(data.raw);
      toast.success("IA analisou a ocorrência");
    } catch (e) {
      toast.error("IA indisponível no momento");
    } finally {
      setAiLoading(false);
    }
  };

  const salvar = async () => {
    if (!form.titulo || !form.descricao_fato) {
      toast.error("Preencha título e descrição");
      return;
    }
    setSaving(true);
    try {
      const { data } = await compliance.criarOcorrencia(form);
      toast.success(`Processo ${data.protocolo} criado`);
      navigate(`/compliance/processos/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up max-w-4xl mx-auto">
        <div className="mb-8">
          <Link to="/compliance/ocorrencias" className="text-[#B28F4B] hover:text-[#D4AF37] text-sm font-semibold flex items-center gap-1 mb-4">
            ← Voltar
          </Link>
          <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.3em] uppercase mb-3">
            Novo Registro
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-light text-white">
            Registro de <span className="text-[#B28F4B] italic font-serif">ocorrência</span>
          </h1>
          <p className="text-[#A1A4C7] mt-2 text-sm">Preencha os dados. Use a IA para classificar automaticamente antes de salvar.</p>
        </div>

        <div className="soeasy-card p-6 md:p-8 space-y-5">
          <Field label="Título" testId="input-titulo">
            <input
              type="text" value={form.titulo}
              onChange={e => handle("titulo", e.target.value)}
              placeholder="Resumo curto e objetivo"
              className="input-field"
              data-testid="input-titulo"
            />
          </Field>

          <Field label="Descrição do fato">
            <textarea
              value={form.descricao_fato}
              onChange={e => handle("descricao_fato", e.target.value)}
              placeholder="Descreva com máxima objetividade o que ocorreu, quando, onde, quem estava envolvido..."
              rows={5}
              className="input-field resize-none"
              data-testid="input-descricao"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Gravidade">
              <select value={form.gravidade} onChange={e => handle("gravidade", e.target.value)} className="input-field" data-testid="select-gravidade">
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="critica">Crítica</option>
              </select>
            </Field>
            <Field label="Data do fato">
              <input type="date" value={form.data_fato} onChange={e => handle("data_fato", e.target.value)} className="input-field" />
            </Field>
            <Field label="Origem">
              <select value={form.origem} onChange={e => handle("origem", e.target.value)} className="input-field">
                <option value="manual">Manual</option>
                <option value="canal_denuncia">Canal de Denúncia</option>
                <option value="auditoria">Auditoria</option>
                <option value="supervisao">Supervisão</option>
                <option value="rh">RH</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Local"><input type="text" value={form.local} onChange={e => handle("local", e.target.value)} className="input-field" placeholder="Local do fato"/></Field>
            <Field label="Setor envolvido"><input type="text" value={form.setor_envolvido} onChange={e => handle("setor_envolvido", e.target.value)} className="input-field" placeholder="Ex: Operações"/></Field>
          </div>

          <Field label="Colaborador investigado (opcional)">
            <input type="text" value={form.colaborador_investigado} onChange={e => handle("colaborador_investigado", e.target.value)} className="input-field" placeholder="Nome completo ou iniciais"/>
          </Field>

          {/* IA Panel */}
          <div className="soeasy-card p-5 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1F2350 0%, #131532 100%)" }}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#B28F4B]/10 blur-[60px] rounded-full pointer-events-none" />
            <div className="relative">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-[#B28F4B]" />
                  <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#B28F4B]">Análise IA · Claude Sonnet 4.6</span>
                </div>
                <button
                  onClick={analisarIA}
                  disabled={aiLoading}
                  data-testid="btn-analisar-ia"
                  className="text-xs px-3 py-1.5 bg-[#B28F4B]/15 border border-[#B28F4B]/40 text-[#B28F4B] hover:bg-[#B28F4B]/25 rounded-lg font-bold flex items-center gap-1.5 transition"
                >
                  {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  Classificar com IA
                </button>
              </div>
              {aiResult ? (
                <pre className="text-[#A1A4C7] text-xs font-mono whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">{aiResult}</pre>
              ) : (
                <div className="text-[#A1A4C7] text-sm">
                  Clique acima para que a IA classifique gravidade, sugira procedimento (PAI / Sindicância / PDI) e avalie risco jurídico.
                </div>
              )}
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.anonimo} onChange={e => handle("anonimo", e.target.checked)} className="w-4 h-4 accent-[#B28F4B]"/>
            <span>Registrar como anônima</span>
          </label>

          <div className="pt-4 flex justify-end gap-3 border-t border-[#B28F4B]/10">
            <Link to="/compliance/ocorrencias" className="px-5 py-3 rounded-lg text-[#A1A4C7] hover:bg-[#131532] font-semibold text-sm">Cancelar</Link>
            <button
              onClick={salvar} disabled={saving}
              data-testid="btn-salvar-ocorrencia"
              className="inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-6 py-3 rounded-lg transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              Registrar Ocorrência
            </button>
          </div>
        </div>
      </div>
      <style>{`.input-field { width: 100%; background: #0A0B1A; border: 1px solid rgba(178,143,75,0.2); color: #fff; padding: 10px 14px; border-radius: 8px; outline: none; transition: border 0.2s; font-family: inherit; font-size: 14px; } .input-field:focus { border-color: #B28F4B; }`}</style>
    </Shell>
  );
}

function Field({ label, children, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">{label}</label>
      {children}
    </div>
  );
}
